import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_NETWORK_MONITOR } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ContentControlNetworkMonitorAttackTechniqueAction,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ContentControlNetworkMonitorFormValue,
  ContentControlNetworkMonitorSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.types';
import {
  CONTENT_CONTROL_NETWORK_MONITOR_DEFAULT_VALUE,
  getAttackTechniquesDefaultValue,
  getContentControlNetworkMonitorSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.values';
import {
  columns,
  ContentControlNetworkMonitorAttackTechniqueKey,
  convertDbValueToFormValue,
  convertFormValueToDbValue,
  rows,
} from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../ui/gzSelectableTableRowGroup';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../ui/gzButton/types';
import { Feature } from '@nirvana/shared/constants';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_NETWORK_MONITOR)
export class GzContentControlNetworkMonitorWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ContentControlNetworkMonitorFormValue,
  ContentControlNetworkMonitorSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.CONTENT_CONTROL,
    sectionId: SectionId.CONTENT_CONTROL_NETWORK_MONITOR,
  };
  protected searchableKeywords = getContentControlNetworkMonitorSearchableKeywords();
  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);

  @query('#content-control-network-monitor-enable-field')
  private networkMonitorEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#content-control-network-monitor-info-text')
  private networkMonitorInfoText: GzTextWebComponent;

  @query('#content-control-network-monitor-server-traffic-scan-field')
  private serverTrafficScanSwitchField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#content-control-network-monitor-inspect-encrypted-domain-controller-traffic-field')
  private inspectEncryptedDomainControllerTrafficField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#content-control-network-monitor-content')
  private networkMonitorContent: HTMLElement;

  @query('#content-control-network-monitor-inspect-rdp-traffic-field')
  private inspectRdpTrafficField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#content-control-network-monitor-attack-techniques-field')
  private attackTechniquesField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<
      ContentControlNetworkMonitorAttackTechniqueKey,
      ContentControlNetworkMonitorAttackTechniqueAction
    >
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="content-control-network-monitor-enable-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[
          ContentControlNetworkMonitorSearchableKeywords.NetworkAttacksDefense
        ].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span
          >${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.NetworkAttacksDefense]
            .translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="content-control-network-monitor-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Network Attack Defense detects attack techniques used to gain unauthorized access to endpoints.')}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="content-control-network-monitor-content">

        <gz-form-field class="mt-2" id="content-control-network-monitor-server-traffic-scan-field">
          <gz-toggle
            gz-form-input
            searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.ServerTrafficScan].label}"
            @toggleChange="${this.onServerTrafficScanToggleChange}"
          >
            <gz-title class="toggle-subtitle text-uppercase" fontSize="11" fontWeight="600">
              ${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.ServerTrafficScan].translatedLabel}
            </gz-text>
          </gz-toggle>
          <gz-text fontSize="12" fontWeight="400" class="infoText mb-2">${translateFn("Monitors incoming traffic on servers to detect potential threats.")}</gz-text>
        </gz-form-field>

        <gz-form-field class="mt-1" id="content-control-network-monitor-inspect-encrypted-domain-controller-traffic-field">
          <gz-checkbox
            gz-form-input
            label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.InspectEncryptedDomainControllerTraffic].translatedLabel}"
            searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.InspectEncryptedDomainControllerTraffic].label}"
          ></gz-checkbox>
        </gz-form-field>


        <gz-form-field class="mt-2" id="content-control-network-monitor-inspect-rdp-traffic-field"
           ?disabled="${this.shouldRdpTrafficInspectionBeDisabled()}">
            <gz-toggle
              gz-form-input
              searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.InspectRdpTraffic].label}"
            >
              <gz-title class="toggle-subtitle text-uppercase" fontSize="11" fontWeight="600">
                ${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.InspectRdpTraffic].translatedLabel}
              </gz-title>
            </gz-toggle>
          <gz-info-box severity='warning' class="${!this.shouldRdpTrafficInspectionBeDisabled() ? 'hidden' : ''}">
            ${translateFn('To enable this option, first activate encrypted traffic interception in Network Protection > General.')}
          </gz-info-box>
        </gz-form-field>

        <gz-title
          fontSize="11"
          fontWeight="600"
          class="form-subtitle"
          searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.AttCkTechniques]
            .label}"
          >${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.AttCkTechniques]
            .translatedLabel}</gz-title
        >

        <gz-text fontSize="12" fontWeight="400" class="infoText"
          >${translateFn(
            "Select the techniques you want to get reports on or block, based on your organization's security requirements."
          )}
        </gz-text>

        <gz-form-field
          class="label-mw-250 mt-2"
          id="content-control-network-monitor-attack-techniques-field"
          label=""
          searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.InitialAccess]
            .label +
          this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.CredentialAccess].label +
          this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.Discovery].label +
          this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.LateralMovement].label +
          this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.Crimeware].label}"
        >
          <gz-selectable-table-row-group
            gz-form-input
            .allowFullRowSelection="${true}"
            .allowFullColumnSelection="${true}"
            .defaultColumn="${this.isCompanionStandalone
              ? ContentControlNetworkMonitorAttackTechniqueAction.REPORT_ONLY
              : ContentControlNetworkMonitorAttackTechniqueAction.BLOCK_PROCESS}"
            ?hasColumnsDisabled="${this.isCompanionStandalone}"
            .rows="${rows()}"
            .columns="${columns()}"
          ></gz-selectable-table-row-group>
        </gz-form-field>

        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight="${ButtonHeights.SMALL}"
          buttonFontSize="${ButtonFontSizes.SMALL}"
          searchable-label="${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.ResetToDefault]
            .label}"
          @click="${this.onResetButtonClick.bind(this)}"
          >${this.searchableKeywords[ContentControlNetworkMonitorSearchableKeywords.ResetToDefault].translatedLabel}
        </gz-button>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.contentControl.networkMonitor
   */
  protected getValueFromManager(): ContentControlNetworkMonitorFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.CONTENT_CONTROL]: {
          [SectionId.CONTENT_CONTROL_NETWORK_MONITOR]: CONTENT_CONTROL_NETWORK_MONITOR_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.CONTENT_CONTROL]: {
          [SectionId.CONTENT_CONTROL_NETWORK_MONITOR]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.contentControl.networkMonitor'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        contentControl: {
          networkMonitor: {
            enable: this.networkMonitorEnableField,
            inspectRdpTraffic: this.inspectRdpTrafficField,
            attackTechniques: this.attackTechniquesField,
            serverTrafficScan: {
              enabled: this.serverTrafficScanSwitchField,
              inspectEncryptedDomainControllerTraffic: this.inspectEncryptedDomainControllerTrafficField,
            }
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set enforced column value for companion standalone
    const enforcedColumnValue = this.isCompanionStandalone
      ? ContentControlNetworkMonitorAttackTechniqueAction.REPORT_ONLY
      : undefined;

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        contentControl: {
          networkMonitor: {
            enable: this.valueT.uiSettings.contentControl.networkMonitor.enable,
            inspectRdpTraffic: this.valueT.uiSettings.contentControl.networkMonitor.inspectRdpTraffic,
            attackTechniques: convertDbValueToFormValue(
              this.valueT.uiSettings.contentControl.networkMonitor.attackTechniques,
              enforcedColumnValue
            ),
            serverTrafficScan: {
              enabled: this.valueT.uiSettings.contentControl.networkMonitor.serverTrafficScan.enabled,
              inspectEncryptedDomainControllerTraffic: this.valueT.uiSettings.contentControl.networkMonitor.serverTrafficScan.inspectEncryptedDomainControllerTraffic,
            }
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    let attackTechniques;
    attackTechniques = _.get(value, ['uiSettings', 'contentControl', 'networkMonitor', 'attackTechniques'], []);
    attackTechniques = convertFormValueToDbValue(attackTechniques);

    // use old value for companion standalone, but update enable with user preferences
    if (this.isCompanionStandalone) {
      attackTechniques = _.cloneDeep(this.valueT?.uiSettings?.contentControl?.networkMonitor?.attackTechniques)?.map(
        item => {
          return {
            ...item,
            enable: attackTechniques.find(attackTechnique => attackTechnique.name === item.name)?.enable ?? item.enable,
          };
        }
      );
    }

    // set attack techniques
    _.set(value, ['uiSettings', 'contentControl', 'networkMonitor', 'attackTechniques'], attackTechniques);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'contentControl', 'networkMonitor'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.networkMonitorContent, !event.detail.checked);
    toggleDisabled(this.networkMonitorInfoText, !event.detail.checked);
  }


  /**
   * Enable/Disable module.
   * @param event
   */
  onServerTrafficScanToggleChange(event: CustomEvent) {
    toggleDisabled(this.inspectEncryptedDomainControllerTrafficField, !event.detail.checked);
  }

  onResetButtonClick() {
    this.attackTechniquesField.value = getAttackTechniquesDefaultValue();
  }

  shouldRdpTrafficInspectionBeDisabled(): boolean {
    const isEncryptedTrafficScanningEnabled: boolean = PolicyPanelManager.getInstance().getPolicy(
      'uiSettings.general.advanced.settings.scanSsl.enabled'
    , false);
    return !isEncryptedTrafficScanningEnabled;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_NETWORK_MONITOR: GzContentControlNetworkMonitorWebComponent;
  }
}
